// -----------------------------------------------------------------------------
// LeadStatusChangedPanel  (LEAD_STATUS_CHANGED)
//
// Mirrors LeadStatusChangedCell.handleMove in TaskChecklistField.jsx. Reads the
// target_status from action_config, shows a "Move to {StatusLabel}" confirm.
// On confirm it POSTs the lead transition, then the /complete/ POST (with
// response_payload.target_status), then calls onComplete(item) as a refresh
// signal — self-contained, same as the cell.
//
// Endpoints (mirror LeadStatusChangedCell.handleMove):
//   1. POST /api/v1/practices/{practiceId}/leads/{leadId}/transition/
//      body { target_status }
//   2. POST /api/v1/practices/{practiceId}/tasks/{taskId}/checklist/{itemId}/complete/
//      body { completion_state: 'YES', response_payload: { target_status } }
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';

// Mirrors STATUS_DISPLAY_LABELS in TaskChecklistField.jsx
const STATUS_DISPLAY_LABELS = {
    NEW: 'New',
    CONSULTATION_BOOKED: 'Consultation Booked',
    CONSULTATION_ATTENDED: 'Consultation Attended',
    FAILED_TO_ATTEND_CONSULTATION: 'Failed to Attend',
    TREATMENT_STARTED: 'Treatment Started',
    CLOSED: 'Completed',
};

export default function LeadStatusChangedPanel({ item, practiceId, leadId, onComplete, onClose }) {
    const [submitting, setSubmitting] = useState(false);

    const actionConfig = item?.raw?.action_config || item?.action_config || {};
    const targetStatus = actionConfig.target_status || null;
    const targetLabel = STATUS_DISPLAY_LABELS[targetStatus] || targetStatus || 'next stage';

    const taskId = item?.taskId ?? item?.rawTask?.id;
    const itemId = item?.itemId ?? item?.id;

    const handleMove = async () => {
        if (submitting) return;
        if (!targetStatus) {
            dispatch(updateError({ success: false, message: 'No target status configured for this item.' }));
            return;
        }
        if (!practiceId || !leadId) {
            dispatch(updateError({ success: false, message: 'Lead context missing — please refresh.' }));
            return;
        }
        setSubmitting(true);
        try {
            // 1) Transition the lead's status.
            const transitionRes = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/transition/`,
                'POST',
                { target_status: targetStatus }
            );
            if (!transitionRes.ok) {
                dispatch(updateError({ success: false, message: transitionRes?.data?.detail || `Failed to move lead to ${targetLabel} — please try again.` }));
                return;
            }
            // 2) Mark the checklist item complete (only if it's a real spawned task).
            if (taskId && itemId) {
                await callApi(
                    `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                    'POST',
                    { completion_state: 'YES', response_payload: { target_status: targetStatus } }
                );
            }
            onComplete?.(item);
        } catch (err) {
            dispatch(updateError({ success: false, message: err?.message || `Failed to move lead to ${targetLabel} — please try again.` }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="lead-status-changed-panel"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: 3,
                py: 2.5,
                gap: 1.75,
                textAlign: 'center',
            }}
        >
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '14px',
                    bgcolor: 'rgba(255,171,0,0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon="eva:swap-outline" width={26} height={26} sx={{ color: '#FFAB00' }} />
            </Box>

            <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    {item?.title || 'Move lead status'}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.5, px: 1 }}>
                    This will move the lead to <strong>{targetLabel}</strong>.
                </Typography>
            </Box>

            <Button
                data-testid="lead-status-changed-move"
                variant="contained"
                disableElevation
                disabled={submitting || !targetStatus}
                startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="eva:arrow-forward-outline" width={16} height={16} />}
                onClick={handleMove}
                sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, bgcolor: '#FFAB00', color: '#1d1d1f', '&:hover': { bgcolor: '#e09a00' }, boxShadow: 'none' }}
            >
                Move to {targetLabel}
            </Button>

            <Button
                variant="text"
                size="small"
                disabled={submitting}
                onClick={onClose}
                sx={{ textTransform: 'none', fontSize: '13px', color: '#6e6e73' }}
            >
                Cancel
            </Button>
        </Box>
    );
}

LeadStatusChangedPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

LeadStatusChangedPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    onComplete: null,
};
